/**
 * Deterministic RNG for Birds of Silk.
 *
 * Single 32-bit seed derived from the day id (e.g. "2026-05-10"). All
 * gameplay randomness MUST draw from this stream. `Math.random()` is
 * banned in `src/sim/`.
 */

/** FNV-1a 32-bit hash → seed. */
export function fnv1a(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32 PRNG. Returns a stateful generator. */
export function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return function rand(): number {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Convenience wrapper: deterministic RNG for a given day id. */
export function rngForDay(dayId: string): () => number {
  return mulberry32(fnv1a(dayId));
}

/** Uniform integer in [min, max] inclusive. */
export function randInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

/** Uniform float in [min, max). */
export function randRange(rng: () => number, min: number, max: number): number {
  return rng() * (max - min) + min;
}
